// Register Page Component
// ========================

import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../ui/Button';
import Input from '../ui/Input';
import { LyraLogo, ContentFrame } from '../ui';

const RegisterPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!displayName.trim()) {
      setError(tErrors('auth.nameRequired'));
      return;
    }

    if (password !== confirmPassword) {
      setError(tErrors('auth.passwordsDontMatch'));
      return;
    }

    if (password.length < 8) {
      setError(tErrors('auth.weakPassword'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(email, password, displayName);
      if (result.success) {
        navigate('/onboarding');
      } else {
        setError(result.error || t('register.defaultError'));
      }
    } catch (err) {
      setError(tErrors('auth.registerFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lyra-language', lang);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo and title - Feature #155: SVG logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <LyraLogo size="lg" showText={true} />
          </div>
          <p className="text-neutral-medium">{tCommon('tagline')}</p>
        </div>

        {/* Register form card - Feature #288: Updated with ContentFrame and new design */}
        <ContentFrame
          variant="border"
          padding="lg"
          showAccent={true}
        >
          {/* Feature #280: Serif display font for headings */}
          <h2 className="text-2xl font-display font-semibold text-neutral-dark dark:text-white mb-6 text-center">
            {t('register.title')}
          </h2>

          {error && (
            <div
              className="mb-4 p-3 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg"
              role="alert"
              aria-live="assertive"
            >
              <p className="text-accent-600 dark:text-accent-400 text-sm text-center font-sans">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                {t('register.name')}
              </label>
              <Input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('register.namePlaceholder')}
                required
                autoComplete="name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                {t('register.email')}
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('register.emailPlaceholder')}
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                {t('register.password')}
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('register.passwordPlaceholder')}
                required
                autoComplete="new-password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                {t('register.confirmPassword')}
              </label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('register.confirmPasswordPlaceholder')}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Language selector */}
            <div className="flex items-center justify-center space-x-2 pt-1">
              <span className="text-sm text-neutral-medium font-sans">{t('register.language')}:</span>
              <button
                type="button"
                onClick={() => handleLanguageChange('da')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  i18n.language.startsWith('da')
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-neutral-medium hover:text-neutral-dark dark:hover:text-gray-300'
                }`}
              >
                {t('register.languageDa')}
              </button>
              <button
                type="button"
                onClick={() => handleLanguageChange('en')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  i18n.language.startsWith('en')
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-neutral-medium hover:text-neutral-dark dark:hover:text-gray-300'
                }`}
              >
                {t('register.languageEn')}
              </button>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? t('register.submitting') : t('register.submit')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-medium text-sm font-sans">
              {t('register.hasAccount')}{' '}
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                {t('register.logIn')}
              </Link>
            </p>
          </div>
        </ContentFrame>
      </div>
    </div>
  );
};

export default RegisterPage;
